import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { HelpCircle } from 'lucide-react';
import { TranslatedText } from '@/components/TranslatedText';
import { FaqAccordion } from '@/components/ui/faq-chat-accordion';

const faqs = [
  {
    id: 1,
    question: "Is this really possible without showing my face?",
    answer: "Yes. The models taught inside rely on niche positioning, structured content systems, and monetization methods that do not require personal branding.",
    icon: "❤️",
    iconPosition: "right" as const,
  },
  {
    id: 2,
    question: "Do I need technical experience?",
    answer: "No. The workflows are built for beginners. Each step is explained clearly, and tools are demonstrated inside the lessons.",
  },
  {
    id: 3,
    question: "How quickly can I see results?",
    answer: "This depends on your execution and niche. Some members launch within weeks. We provide realistic timelines and clear milestones for each business model.",
  },
  {
    id: 4,
    question: "What's included in the free plan?",
    answer: "The free plan gives you access to introductory content with 10 AI credits so you can explore the platform before upgrading.",
  },
  {
    id: 5,
    question: "What if I'm not satisfied?",
    answer: "We offer a 7-day money-back guarantee. Contact us for a full refund — no questions asked.",
  },
  {
    id: 6,
    question: "Do you have referral programs?",
    answer: "Yes. Members can refer others and earn through our structured referral program — another faceless income stream with no ceiling.",
  },
];

export const LightFAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} id="faq" className="py-12 sm:py-20 px-4 bg-background">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-primary/5 border border-primary/15 mb-5">
            <HelpCircle className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">FAQ</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
            <TranslatedText>Common Questions</TranslatedText>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <FaqAccordion data={faqs} className="max-w-lg" />
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 text-center p-5 rounded-xl bg-muted border border-border"
        >
          <p className="text-muted-foreground text-sm mb-1">
            <TranslatedText>Still have questions?</TranslatedText>
          </p>
          <a 
            href="mailto:hello@auradynamics.management"
            className="text-primary font-medium text-sm hover:text-primary/80 transition-colors"
          >
            hello@auradynamics.management
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default LightFAQSection;
